import { CONFIG, type GameConfig } from "../config.js";
import { distance, inZone } from "../map/collision.js";
import type { DeskDef, Vec2, ZoneDef } from "../map/types.js";
import type { InteractionErrorCode, ProjectPhase } from "../protocol.js";
import { getRole, type RoleId } from "../roles.js";
import type { Activity } from "./exhaustion.js";

export type Check = { ok: true } | { ok: false; code: InteractionErrorCode };

export const OK: Check = { ok: true };

function fail(code: InteractionErrorCode): Check {
  return { ok: false, code };
}

export interface InteractionPlayer {
  pos: Vec2;
  activity: Activity;
  burnedOut: boolean;
  exhaustion: number;
  role: RoleId;
}

export interface CanWorkInput {
  player: InteractionPlayer;
  desk: DeskDef;
  phase: ProjectPhase;
  /** Session id currently occupying the desk, if any. */
  occupantId?: string;
  config?: GameConfig;
}

/**
 * Whether a player may start working at a desk. Evaluate in this order so the
 * most actionable message wins.
 */
export function canWork(input: CanWorkInput): Check {
  const c = input.config ?? CONFIG;
  const { player, desk } = input;

  if (player.activity !== "idle") return fail("ALREADY_BUSY");
  if (distance(player.pos, desk.interact) > c.INTERACT_RADIUS_PX) return fail("OUT_OF_RANGE");

  const role = getRole(player.role);
  if (role.workstation !== desk.workstationType) return fail("WRONG_ROLE");
  if (input.phase !== "active") return fail("PHASE_CELEBRATING");
  if (input.occupantId) return fail("DESK_TAKEN");
  if (player.burnedOut) return fail("BURNED_OUT");
  if (player.exhaustion >= c.MAX_EXHAUSTION_S) return fail("EXHAUSTED_MAX");

  return OK;
}

export interface CanDrinkInput {
  player: InteractionPlayer;
  relaxZones: ZoneDef[];
  config?: GameConfig;
}

export function canDrinkCoffee(input: CanDrinkInput): Check {
  const { player } = input;

  if (player.activity !== "idle") return fail("ALREADY_BUSY");
  if (!input.relaxZones.some((z) => inZone(player.pos, z))) return fail("NOT_IN_RELAX_ZONE");
  if (player.exhaustion <= 0) return fail("NOT_TIRED");

  return OK;
}

export function inExitZone(pos: Vec2, exitZone: ZoneDef | undefined): boolean {
  if (!exitZone) return false;
  return inZone(pos, exitZone);
}

/** Nearest desk whose interaction point is within `radius`, or `undefined`. */
export function nearestDesk(
  pos: Vec2,
  desks: DeskDef[],
  radius: number = CONFIG.INTERACT_RADIUS_PX,
): DeskDef | undefined {
  let best: DeskDef | undefined;
  let bestDist = Number.POSITIVE_INFINITY;
  for (const desk of desks) {
    const d = distance(pos, desk.interact);
    if (d <= radius && d < bestDist) {
      best = desk;
      bestDist = d;
    }
  }
  return best;
}

export function deskById(desks: DeskDef[], id: string | undefined): DeskDef | undefined {
  if (!id) return undefined;
  return desks.find((d) => d.id === id);
}
